import './options.scss';

import { FieldArray, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { FaArrowAltCircleLeft } from 'react-icons/fa';
import { MdOutlineArrowForwardIos } from 'react-icons/md';

import { useModalReturn } from '@/resources/hooks/useModal';
import { getAnswerType, insertSelectOption } from '@/services/options';

import { generateRandomKey } from '../../../../services/global';
import {
  ICartItemValues,
  ICartProduct,
  IQuestion as IQuestionOrder,
} from '../../../../store/ducks/order/types';
import { IAnswer, IProduct, IQuestion } from '../../../../store/ducks/products/types';
import { OptionContext } from '../../contexts/OptionsContext';
import CustomModal from '../../global/CustomModal';
import OptionsImage from './components/OptionsImage';
import QuantityPreview from './components/QuantityPreview';
import OptionsAnswers from './OptionsAnswers';

interface Props {
  options: IQuestion[];
  product: ICartProduct | IProduct;
  action: 'add' | 'receive';
  modal: useModalReturn;
}

const Options = ({ modal, options, product, action }: Props) => {
  const [step, setStep] = useState<number>(0);
  const { values, submitForm, setFieldTouched, isSubmitting } = useFormikContext<ICartItemValues>();

  const nextStep = step + 1;
  const isTheLastStep = step === options.length - 1;

  const [showModal, setShowModal] = useState<boolean>(modal.show);

  useEffect(() => setShowModal(modal.show), [modal.show]);

  const validateMinOfAnswers = (values: ICartItemValues) => {
    let answers = 0;
    const option = values.options[step];
    const minOfAnswers = options[step]?.minimum_of_answers;

    if (option) {
      option.select_options.forEach((answer) => {
        const question = options[step];

        if (question) {
          const type = getAnswerType(question);

          if (type !== 'additionals' && answer.chosen) answers += 1;
          if (type === 'additionals' && parseInt(answer.quantity) > 0)
            answers += parseInt(answer.quantity);
        }
      });
    }

    return minOfAnswers && answers < minOfAnswers;
  };

  const optionsTip = (option: IQuestion | undefined) => {
    if (!option) return '';

    const type = getAnswerType(option);
    const { minimum_of_answers, maximum_of_answers } = option;

    if (minimum_of_answers === 0 && maximum_of_answers === 0) return '';
    if (type === 'radio') return 'Escolha 1 opção';
    if (minimum_of_answers === 0)
      return `Escolha no máximo ${maximum_of_answers} ${
        maximum_of_answers === 1 ? 'opção' : 'opções'
      }`;
    return `Escolha entre ${minimum_of_answers} e ${maximum_of_answers} opções`;
  };

  return (
    <FieldArray name={`options`} key={`options-${step}-${generateRandomKey()}`}>
      {({ insert, remove, replace }) => {
        const insertOption = () => {
          const nextOption = options[nextStep];

          insert(nextStep, {
            select_options: nextOption?.select_options.map((answer) =>
              insertSelectOption({ ...answer, chosen: answer.marked_as_default }, nextOption),
            ),
            add_order_quantity: nextOption?.add_order_quantity,
          });
        };

        const updateOption = (values: ICartItemValues) => {
          const orderForm: IQuestionOrder | undefined = values.options[step];
          const option = options[step];

          replace(step, {
            select_options: option?.select_options.map((answer) => {
              const type = getAnswerType(option);
              const selectedOption = orderForm?.select_options.find(
                (selectOption) =>
                  selectOption.id === answer.id &&
                  (type === 'additionals'
                    ? parseInt(selectOption.quantity) > 0
                    : selectOption.chosen),
              );

              return selectedOption
                ? insertSelectOption(
                    { ...answer, chosen: true, quantity: selectedOption.quantity },
                    option,
                  )
                : insertSelectOption({ ...answer, chosen: false }, option);
            }),
            add_order_quantity: option?.add_order_quantity,
          });
        };

        return (
          <div>
            <CustomModal
              show={showModal}
              onHide={() => {
                setStep(0);

                setShowModal(false);
              }}
              centered={true}
              key={`options-${step}-${generateRandomKey()}`}
              className="options-modal"
              size="xl"
            >
              <Modal.Body className="p-2">
                <div className=" d-flex flex-wrap options-wrapper">
                  <OptionsImage product={product} />

                  <div className="col-12 col-md-8 options-body">
                    <div className="options-header">
                      <span className="d-flex align-items-center">
                        {step > 0 && (
                          <FaArrowAltCircleLeft
                            size="28"
                            className="mr-2 cursor-pointer text-primary"
                            onClick={() => {
                              if (action === 'add') remove(step);

                              setFieldTouched(`options.${step - 1}.select_options.id`, true);
                              setStep(step - 1);
                            }}
                          />
                        )}

                        <span className="product-title">{product.title}</span>
                      </span>

                      {options[step]?.title && (
                        <span className="options-description">{options[step]?.title}</span>
                      )}
                    </div>

                    <div className="options-list py-3">
                      <FieldArray
                        name={`options.${step}.select_options`}
                        key={`options-123-${step}-${generateRandomKey()}`}
                      >
                        {() => {
                          const option = options[step];
                          const type = option ? getAnswerType(option) : null;

                          return (
                            <>
                              {values.options[step]?.select_options.map((_, index) => {
                                const question = options[step];
                                const answer: IAnswer | undefined = question?.select_options[index];

                                if (question && answer) {
                                  return (
                                    <OptionContext.Provider
                                      value={{
                                        action,
                                        answer,
                                        optionIndex: step,
                                        selectOptionIndex: index,
                                        type: getAnswerType(question),
                                        maxOfAnswers: question.maximum_of_answers,
                                        product,
                                        optionTitle: question.title,
                                      }}
                                      key={`options-${step}-select-options-${index}`}
                                    >
                                      <OptionsAnswers />
                                    </OptionContext.Provider>
                                  );
                                }

                                return null;
                              })}

                              {type === 'additionals' && option?.add_order_quantity && (
                                <QuantityPreview product={product} />
                              )}
                            </>
                          );
                        }}
                      </FieldArray>
                    </div>

                    <div className="options-footer">
                      <span className="mb-0 options-info">{optionsTip(options[step])}</span>

                      <Button
                        size="sm"
                        variant="success"
                        disabled={validateMinOfAnswers(values) || isSubmitting}
                        onClick={() => {
                          if (isTheLastStep) {
                            submitForm();

                            setStep(0);
                            setShowModal(false);
                          } else {
                            if (action === 'add') insertOption();
                            else updateOption(values);

                            setFieldTouched(`options.${nextStep}`, false);

                            setStep(step + 1);
                          }
                        }}
                      >
                        {isSubmitting ? (
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          <span className="d-flex align-items-center">
                            {!isTheLastStep && <MdOutlineArrowForwardIos size={14} />}

                            <span className="px-3">{isTheLastStep ? 'Salvar' : 'Avançar'}</span>
                          </span>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </Modal.Body>
            </CustomModal>
          </div>
        );
      }}
    </FieldArray>
  );
};

Options.displayName = 'Options';

export default Options;
